
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { BrandedBackground, BrandedText } from '@/components/shared/ui';

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface AlreadyRespondedStateProps {
  candidateStatus: {
    status: string;
    candidate_name: string;
    target_position: string;
    response_type?: string;
    refusal_reason?: string;
    submitted_at?: string;
  };
  branding?: BrandingSettings | null;
}

export const AlreadyRespondedState = ({ candidateStatus, branding }: AlreadyRespondedStateProps) => {
  const isRefused = candidateStatus.status === 'refused';
  const isCompleted = candidateStatus.status === 'completed';

  const getIcon = () => {
    if (isRefused) return <XCircle className="h-12 w-12 text-red-500" />;
    if (isCompleted) return <CheckCircle className="h-12 w-12 text-green-500" />;
    return <Clock className="h-12 w-12 text-blue-500" />;
  };

  const getTitle = () => {
    if (isRefused) return 'Participation refusée';
    if (isCompleted) return 'Référents déjà soumis';
    return 'Participation acceptée';
  };

  const getMessage = () => {
    if (isRefused) {
      return (
        <div>
          <p className="text-gray-600 mb-2">
            Vous avez déjà refusé de participer à cette prise de référence.
          </p>
          {candidateStatus.refusal_reason && (
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Motif :</strong> {candidateStatus.refusal_reason}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (isCompleted) {
      return (
        <p className="text-gray-600">
          Vous avez déjà soumis votre liste de référents pour cette campagne. 
          Le processus est maintenant en cours avec vos référents.
        </p>
      );
    }

    return (
      <p className="text-gray-600">
        Vous avez déjà accepté de participer à cette prise de référence, 
        mais vous n'avez pas encore soumis votre liste de référents.
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {getIcon()}
              {getTitle()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <BrandedBackground
              branding={branding}
              opacity={0.1}
              className="p-4 rounded-lg"
            >
              <BrandedText 
                className="font-medium mb-2"
                branding={branding}
                as="h3"
              >
                Détails de la demande
              </BrandedText>
              <div className="space-y-1 text-sm">
                <p><strong>Candidat :</strong> {candidateStatus.candidate_name}</p>
                <p><strong>Poste visé :</strong> {candidateStatus.target_position}</p>
                {candidateStatus.submitted_at && (
                  <p><strong>Date de réponse :</strong> {new Date(candidateStatus.submitted_at).toLocaleDateString('fr-FR')}</p>
                )}
              </div>
            </BrandedBackground>

            <div>
              {getMessage()}
            </div>

            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              <p>
                Si vous pensez qu'il s'agit d'une erreur ou si vous souhaitez modifier votre réponse, 
                veuillez contacter directement le recruteur.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};